/**
 * Avatar dei canali di supporto: emoji (un solo grafema) oppure immagine
 * caricata (URL relativo `/uploads/support-groups/…`, 128px WebP). Senza
 * avatar ogni superficie mostra la sigla colorata di sempre (`channelPalette`).
 */
import { photoUrl } from '../../utils/photoUrl';

export interface ChannelAvatarLike {
  kind?: 'emoji' | 'image' | string | null;
  emoji?: string | null;
  url?: string | null;
}

export type ResolvedChannelAvatar = { kind: 'emoji'; emoji: string } | { kind: 'image'; src: string };

/** Avatar pronto per il rendering (URL immagine assoluto) o null se assente/incompleto. */
export const resolveChannelAvatar = (a?: ChannelAvatarLike | null): ResolvedChannelAvatar | null => {
  if (!a || !a.kind) return null;
  if (a.kind === 'emoji' && a.emoji && a.emoji.trim()) return { kind: 'emoji', emoji: a.emoji.trim() };
  if (a.kind === 'image') {
    // `blob:` = anteprima locale nel form, prima del caricamento.
    const src = a.url && a.url.startsWith('blob:') ? a.url : photoUrl(a.url);
    if (src) return { kind: 'image', src };
  }
  return null;
};

/** Emoji del canale + spazio, per i testi dove non si possono mettere immagini (es. `<option>`). */
export const channelEmojiPrefix = (a?: ChannelAvatarLike | null): string => {
  const r = resolveChannelAvatar(a);
  return r && r.kind === 'emoji' ? `${r.emoji} ` : '';
};

// `new RegExp` invece del letterale: il target TS e es5, il flag `u` lo gestisce Babel/il browser.
const PICTOGRAPHIC = new RegExp('\\p{Extended_Pictographic}', 'u');
const FLAG = new RegExp('^\\p{Regional_Indicator}{2}$', 'u');
const JOINERS = new RegExp('[\\u200d\\ufe0f\\u{1f3fb}-\\u{1f3ff}]', 'gu');

const graphemeCount = (s: string): number => {
  const Seg = (Intl as any).Segmenter;
  if (typeof Seg === 'function') return Array.from(new Seg('it', { granularity: 'grapheme' }).segment(s)).length;
  // Fallback grossolano (browser senza Segmenter): il backend rivalida comunque.
  return Array.from(s.replace(JOINERS, '')).length === 1 ? 1 : 2;
};

/** Lunghezza massima (code unit UTF-16): uguale al backend e al maxLength del form. */
export const MAX_EMOJI_LENGTH = 16;

/** Stessa regola del backend (`utils/supportGroupAvatar.js`): una sola emoji. */
export const isSingleEmoji = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const s = value.trim();
  if (!s || s.length > MAX_EMOJI_LENGTH) return false;
  if (graphemeCount(s) !== 1) return false;
  return PICTOGRAPHIC.test(s) || FLAG.test(s);
};

/** Emoji suggerite nel form del canale (temi tipici dei canali di supporto). */
export const SUGGESTED_CHANNEL_EMOJIS: readonly string[] = [
  '🎧', '💬', '📄', '📋', '🚗', '🏍️', '🪪', '⚖️', '💶', '🧾', '🛠️', '🎓', '📣', '❓', '⭐', '🔔',
];

/** true se il canale ha un avatar utilizzabile (emoji o immagine). */
export const hasChannelAvatar = (ch?: { avatar?: ChannelAvatarLike | null } | null): boolean =>
  resolveChannelAvatar(ch?.avatar) !== null;
